import { facebookPixelId1, facebookPixelId2 } from '@/utils';
import * as Sentry from '@sentry/react';
import { init } from '@web/common';
import store, { persistor } from '@web/common/src/store';
import { nativeApi } from '@web/common/src/utils/nativeApi';
import { sensorsTrack } from '@web/common/src/utils/shenceTrack';
import 'dayjs/locale/zh-hk.js';
import { parse, stringify } from 'query-string';
import React, { StrictMode } from 'react';
import ReactDOM from 'react-dom';
import ReactPixel from 'react-facebook-pixel';
import ReactGA from 'react-ga4';
import TagManager from 'react-gtm-module';
import { Provider } from 'react-redux';
import {
  BrowserRouter as Router,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import App from './App';
import './styles/index.less';
import { isEn } from './utils';
import { initLanguage } from './utils/languageutils';
import { VHKUnhandleRejection } from './utils/unhandleRejection';

nativeApi.init?.({
  initLangCallBack: initLanguage,
});

VHKUnhandleRejection();

// 初始化神策
sensorsTrack(import.meta.env.VITE_APP_SHENCE_SERVER_URL as string);

// 初始化各种埋点
const initTracking = () => {
  setTimeout(() => {
    // GA4
    ReactGA.initialize('G-Y92ZTVPBJ9');
    // fb pixel
    ReactPixel.init(facebookPixelId1);
    ReactPixel.init(facebookPixelId2);
    ReactPixel.pageView(); // For tracking page view
    // GTM
    const tagManagerArgs = {
      gtmId: 'GTM-5RSHVKS',
    };
    TagManager.initialize(tagManagerArgs);
  }, 500);
};

const addLoadEvent = (fun: () => void) => {
  const oldLoad = window.onload as (arg: any) => void;
  if (typeof oldLoad !== 'function') {
    window.onload = fun;
  } else {
    window.onload = (arg: any) => {
      oldLoad && oldLoad(arg);
      fun();
    };
  }
};

addLoadEvent(initTracking);

if (
  // import.meta.env.VITE_APP_ENV === 'development' &&
  String(import.meta.env.VITE_APP_STARTSENTRY) === 'true'
) {
  Sentry.init({
    dsn: import.meta.env.VITE_APP_SENTRY_DSN,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.httpClientIntegration(),
      Sentry.replayIntegration(),
    ],
    // app_js_bridge:true,
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1,
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    release: import.meta.env.VITE_APP_RELEASE,
    beforeSend(event) {
      return event;
    },
    environment: import.meta.env.VITE_APP_ENV,
    normalizeDepth: 5,
    debug: import.meta.env.VITE_APP_ENV === 'development',
  });
  init({
    logErrors: true,
  });
}

document.body.className = `${document.body.className} ${isEn() ? 'en' : ''}`;

ReactDOM.render(
  <StrictMode>
    <Router>
      <QueryParamProvider
        adapter={ReactRouter6Adapter}
        options={{
          searchStringToObject: parse,
          objectToSearchString: stringify,
        }}
      >
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <App />
          </PersistGate>
        </Provider>
      </QueryParamProvider>
    </Router>
  </StrictMode>,
  document.querySelector('#root')
);
